/**
 * Quick-log natural-capture adapter.
 *
 * Thin contract wrapper around the existing structured quick-log extraction path.
 *
 * @param {import('./naturalCaptureContract').NaturalCaptureAdapterInput & { text?: string }} adapterInput
 * @param {Object} options
 * @param {Object} deps
 * @returns {import('./naturalCaptureContract').NaturalCaptureAdapterResult}
 */
export const runNaturalCaptureQuickLogAdapter = (adapterInput = {}, {
  defaultDate = new Date(),
  parserMode = 'quick_log',
  sourceMode = 'quick_log',
  inputMode = 'quick_log',
} = {}, deps = {}) => {
  const text = String(adapterInput.text || '').trim();
  if (!text) {
    return {
      rows: [],
      debug: {
        parserUsed: null,
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'quick_log_structured',
        inputType: 'unsupported',
        reason: 'empty_text',
        rawTextLength: 0,
      },
    };
  }

  const {
    extractQuickNoteEntities,
    filterQuickNoteCaptureableEntities,
    normalizeQuickNoteReviewRows,
    groupRowsForReview,
  } = deps || {};

  const entities = typeof extractQuickNoteEntities === 'function'
    ? extractQuickNoteEntities(text)
    : [];
  const captureableEntities = typeof filterQuickNoteCaptureableEntities === 'function'
    ? filterQuickNoteCaptureableEntities(entities)
    : entities;

  if (!Array.isArray(captureableEntities) || !captureableEntities.length) {
    return {
      rows: [],
      debug: {
        parserUsed: 'extractQuickNoteEntities',
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'quick_log_structured',
        inputType: 'small_structured_review',
        reason: 'no_captureable_entities',
        rawTextLength: text.length,
      },
    };
  }

  const groupedRows = typeof groupRowsForReview === 'function'
    ? groupRowsForReview(captureableEntities.map((entity, index) => {
        const rowDate = new Date(defaultDate);
        if (entity?.time) {
          const [hoursRaw, minutesRaw] = String(entity.time).split(':');
          const hours = Number(hoursRaw);
          const minutes = Number(minutesRaw);
          if (Number.isFinite(hours) && Number.isFinite(minutes)) {
            rowDate.setHours(hours, minutes, 0, 0);
          }
        }

        const entryText = String(entity?.text || '').trim();
        const entryCategory = String(entity?.category || 'log').trim() || 'log';

        return {
          id: `quick-note-${Date.now()}-${index}`,
          date: rowDate,
          note: entryText,
          body: entryText,
          text: entryText,
          content: entryText,
          description: entryText,
          summary: entryText,
          details: entryText,
          value: entryText,
          title: entity?.title || entryText,
          titlePrefix: entity?.titlePrefix || entity?.label || entryCategory,
          category: entryCategory,
          importance: 'normal',
          childName: null,
          include: true,
          reviewConfidence: 'high',
          reviewConfidenceLabel: 'Looks right',
          reviewConfidenceReasons: [],
          timeSource: entity?.time ? 'explicit' : 'missing',
          localTime: entity?.time || null,
          timeBucket: null,
          timeInferred: false,
          timeInferenceReason: '',
          metadata: {
            captureMode: 'small_structured_review',
            routingReason: captureableEntities.length > 1 ? 'multiple_local_events' : 'structured_event_signals',
            localConfidence: 'high',
            usedBackendExtraction: false,
          },
          sourceText: text,
          originalText: text,
          parsedObservation: entryCategory === 'mood' || entryCategory === 'sleep',
          parsedSourceCategory: entryCategory,
          parsedSourceType: entryCategory,
          parsedSourceConfidence: 'high',
        };
      }))
    : captureableEntities;

  const rows = typeof normalizeQuickNoteReviewRows === 'function'
    ? normalizeQuickNoteReviewRows(groupedRows, {
        parserMode,
        sourceMode,
        inputMode,
        rawText: text,
        captureMode: 'small_structured_review',
        routingReason: captureableEntities.length > 1 ? 'multiple_local_events' : 'structured_event_signals',
        localConfidence: 'high',
        usedBackendExtraction: false,
      })
    : groupedRows;

  return {
    rows,
    debug: {
      parserUsed: 'extractQuickNoteEntities',
      parserMode,
      sourceMode,
      inputMode,
      adapterMode: 'quick_log_structured',
      inputType: 'small_structured_review',
      detectedShape: adapterInput.shapeHint || 'school_text',
      chunkPlanSource: 'extractQuickNoteEntities',
      rawTextLength: text.length,
      rowCount: rows.length,
      reason: rows.length ? 'structured_event_signals' : 'no_captureable_entities',
      decisionAudit: [],
      categoryComparisons: [],
    },
  };
};
